import React, { useState, useEffect } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useApp } from '../context/AppContext';
import StripePayment from './StripePayment';
import { createPortalSession } from '../services/stripeService';

const PaymentPage = () => {
  const { 
    subscription, 
    getCurrentTier, 
    isLiteTier, 
    isPremiumTier,
    getVehicleLimit,
    isTrialActive,
    getTrialDaysRemaining,
    syncSubscriptionFromStripe,
    SUBSCRIPTION_TIERS
  } = useSubscription();
  
  const { vehicles } = useApp();
  const [showStripePayment, setShowStripePayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanType, setSelectedPlanType] = useState(null);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);

  const currentTier = getCurrentTier();
  const vehicleCount = vehicles.length; // Count ALL vehicles, not just Sales
  const vehicleLimit = getVehicleLimit();
  const trialDaysRemaining = getTrialDaysRemaining();

  // Display labels and prices that match the plan cards above
  const PLAN_DISPLAY = {
    premium: { name: 'Premium Plan', price: '249.99' },
    lite: { name: 'Lite Plan', price: '199.99' },
  };
  const currentPlanDisplay = PLAN_DISPLAY[currentTier.id] || PLAN_DISPLAY.lite;
  const isLiteTrial = currentTier.id === 'lite' && (isTrialActive() || !subscription?.syncedFromStripe);
  const currentPlanPrice = isLiteTrial ? '0.00' : currentPlanDisplay.price;
  const statusLabel = isTrialActive()
    ? (trialDaysRemaining > 0 ? `Trial (${trialDaysRemaining} days left)` : 'Trial')
    : subscription?.status === 'active'
    ? 'Active'
    : subscription?.status === 'canceled'
    ? 'Canceled'
    : subscription?.status === 'past_due'
    ? 'Past Due'
    : subscription?.status === 'trialing'
    ? 'Trial'
    : subscription?.status || 'Active';

  // Get user ID from sessionStorage
  const getUserId = () => {
    return sessionStorage.getItem('userId') || sessionStorage.getItem('firebaseUid');
  };

  // Sync subscription from Stripe on mount and after portal return
  useEffect(() => {
    const syncSubscription = async () => {
      const userId = getUserId();
      if (userId) {
        await syncSubscriptionFromStripe(userId);
      }
    };

    // Check if returning from Stripe Customer Portal
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('portal_return')) {
      syncSubscription();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Initial sync - only run once on mount, not on every render
      // The SubscriptionContext already handles initial sync, so we can skip this
      // to avoid duplicate calls
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const handleManageSubscription = async () => {
    try {
      setIsManagingSubscription(true);
      const userId = getUserId();
      if (!userId) {
        alert('User not authenticated. Please log in.');
        return;
      }

      const portalSession = await createPortalSession(userId);
      if (portalSession && portalSession.url) {
        window.location.href = portalSession.url;
      }
    } catch (error) {
      console.error('Failed to create portal session:', error);
      alert('Failed to open subscription management. Please try again.');
    } finally {
      setIsManagingSubscription(false);
    }
  };

  const handleUpgrade = (planType = 'premium') => {
    setSelectedPlanType(planType);
    setShowStripePayment(true);
    // Scroll to payment section after a brief delay to allow state update
    setTimeout(() => {
      const paymentSection = document.getElementById('payment-form-section');
      if (paymentSection) {
        paymentSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleStripePaymentSuccess = async (response) => {
    console.log('Stripe payment successful:', response);
    setShowStripePayment(false);
    
    // Sync subscription after successful payment
    const userId = getUserId();
    if (userId) {
      await syncSubscriptionFromStripe(userId);
    }
    
    const planName = selectedPlanType === 'lite' ? 'Lite' : 'Premium';
    alert(`Payment successful! Your subscription has been updated to ${planName} plan.`);
  };

  const handleStripePaymentError = (error) => {
    console.error('Stripe payment error:', error);
    // Error is already shown in the component
  };

  const handleStripePaymentCancel = () => {
    setShowStripePayment(false);
  };

  return (
    <div className="billing-page">
      <div className="billing-header">
        <h1 className="billing-title">Billing</h1>
        <p className="billing-subtitle">Overview of your accounts</p>
      </div>

      <div className="billing-content">
        {/* Subscription Plans Section */}
        <div className="subscription-plans-section">
          <h2>Subscription Plans</h2>
          <p>Choose the plan that works best for your business</p>
          
          <div className="plans-grid">
            {/* Lite Plan */}
            <div 
              className={`subscription-card compact lite clickable-plan-card ${isLiteTier() ? 'current' : ''}`}
              onClick={(e) => {
                if (!e.target.closest('.upgrade-buttons')) {
                  setSelectedPlan('lite');
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!e.target.closest('.upgrade-buttons')) {
                    setSelectedPlan('lite');
                  }
                }
              }}
            >
              <div className="tier-badge lite centered">
                {isLiteTier() && (isTrialActive() || !subscription?.syncedFromStripe) ? 'Trial' : 'Monthly'}
              </div>
              <h3>{isLiteTier() && (isTrialActive() || !subscription?.syncedFromStripe) ? 'Trial Plan' : 'Lite Plan'}</h3>
              <div className="price">
                <span className="currency">£</span>
                <span className="amount">
                  {isLiteTier() && (isTrialActive() || !subscription?.syncedFromStripe) ? '0.00' : '199.99'}
                </span>
                <span className="period">/month</span>
              </div>

              <div className="features-list compact">
                <ul>
                  {SUBSCRIPTION_TIERS.LITE.features.slice(0, 6).map((feature, index) => (
                    <li key={index}>
                      <span className="checkmark">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {!isLiteTier() ? (
                <div className="upgrade-buttons">
                  <button
                    className="upgrade-btn primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpgrade('lite');
                    }}
                    disabled={showStripePayment}
                  >
                    Subscribe
                  </button>
                </div>
              ) : (
                <div className="upgrade-buttons">
                  <div className="current-plan-badge">Current Plan</div>
                </div>
              )}
            </div>

            {/* Premium Plan */}
            <div 
              className={`subscription-card compact premium clickable-plan-card ${!isLiteTier() ? 'current' : ''}`}
              onClick={(e) => {
                // Only open modal if not clicking on buttons
                if (!e.target.closest('.upgrade-buttons')) {
                  setSelectedPlan('premium');
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!e.target.closest('.upgrade-buttons')) {
                    setSelectedPlan('premium');
                  }
                }
              }}
            >
              <div className="tier-badge premium centered">Monthly</div>
              <h3>Premium Plan</h3>
              <div className="price">
                <span className="currency">£</span>
                <span className="amount">249.99</span>
                <span className="period">/month</span>
              </div>

              {!isLiteTier() && (
                <div className="usage-stats">
                  <div className="stat">
                    <span className="label">Vehicles:</span>
                    <span className="value">{vehicleCount} / Unlimited</span>
                  </div>
                </div>
              )}

              <div className="features-list compact">
                <ul>
                  <li key="no-contract">
                    <span className="checkmark">✓</span>
                    No contract
                  </li>
                  {SUBSCRIPTION_TIERS.PREMIUM.features.slice(0, 6).map((feature, index) => (
                    <li key={index}>
                      <span className="checkmark">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {isLiteTier() ? (
                <div className="upgrade-buttons">
                  <button
                    className="upgrade-btn primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpgrade();
                    }}
                    disabled={showStripePayment}
                  >
                    Upgrade Now
                  </button>
                </div>
              ) : (
                <div className="upgrade-buttons">
                  <div className="current-plan-badge">Current Plan</div>
                </div>
              )}
            </div>

            {/* Premium Plan - 12 Month Contract */}
            <div 
              className={`subscription-card compact premium plan-annual clickable-plan-card ${!isLiteTier() ? 'current' : ''}`}
              onClick={(e) => {
                if (!e.target.closest('.upgrade-buttons')) {
                  setSelectedPlan('premium');
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!e.target.closest('.upgrade-buttons')) {
                    setSelectedPlan('premium');
                  }
                }
              }}
            >
              <div className="tier-badge premium tier-badge-annual centered">Contract</div>
              <h3>Premium Plan</h3>
              <div className="price price-annual">
                <span className="currency">£</span>
                <span className="amount">199.99</span>
                <span className="period">/month</span>
              </div>
              <p className="price-note">Billed monthly on a 12 month minimum term</p>
              <p className="plan-annual-savings">Save £600 by signing up to Premium</p>

              {!isLiteTier() && (
                <div className="usage-stats">
                  <div className="stat">
                    <span className="label">Vehicles:</span>
                    <span className="value">{vehicleCount} / Unlimited</span>
                  </div>
                </div>
              )}

              <div className="features-list compact">
                <ul>
                  {SUBSCRIPTION_TIERS.PREMIUM.features.slice(0, 7).map((feature, index) => (
                    <li key={index}>
                      <span className="checkmark">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {isLiteTier() ? (
                <div className="upgrade-buttons">
                  <button
                    className="upgrade-btn primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpgrade();
                    }}
                    disabled={showStripePayment}
                  >
                    Upgrade Now
                  </button>
                </div>
              ) : (
                <div className="upgrade-buttons">
                  <div className="current-plan-badge">Current Plan</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="billing-grid">
          {/* Subscription Details Card */}
          <div className="billing-card">
            <div className="card-header">
              <h3>Subscription Details</h3>
              <p>Manage your subscription and billing information</p>
            </div>
            
            <div className="payment-info">
              {subscription?.syncedFromStripe && (
                <>
                  <div className="next-invoice">
                    <span className="label">Next billing date:</span>
                    <span className="value">
                      {subscription?.nextBillingDate 
                        ? new Date(subscription.nextBillingDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })
                        : '-'
                      }
                    </span>
                  </div>
                  
                  <div className="subscription-management" style={{ marginTop: '24px' }}>
                    <button 
                      className="primary-btn"
                      onClick={handleManageSubscription}
                      disabled={isManagingSubscription}
                      style={{ width: '100%', marginBottom: '12px' }}
                    >
                      {isManagingSubscription ? 'Opening...' : 'Manage Subscription'}
                    </button>
                    <p style={{ 
                      fontSize: '14px', 
                      color: '#666', 
                      margin: 0,
                      textAlign: 'center',
                      lineHeight: '1.5'
                    }}>
                      Update payment method, view invoices, and manage billing in Stripe Customer Portal
                    </p>
                  </div>
                </>
              )}
              
              {!subscription?.syncedFromStripe && (
                <div className="subscription-setup" style={{ padding: '20px 0', textAlign: 'center' }}>
                  <p style={{ 
                    fontSize: '14px', 
                    color: '#666', 
                    margin: 0 
                  }}>
                    Subscribe to a plan to access subscription management
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Current Plan Card */}
          <div className="billing-card">
            <div className="card-header">
              <h3>Current Plan</h3>
              <p>Your current subscription</p>
            </div>
            
            <div className="products-list">
              <div className="product-item">
                <div className="product-info">
                  <div className="product-name">{isLiteTrial ? 'Trial Plan' : currentPlanDisplay.name}</div>
                  <div className="product-details">
                    <span>£{currentPlanPrice}/month</span>
                    {vehicleLimit && (
                      <span> • {vehicleCount}/{vehicleLimit} vehicles used</span>
                    )}
                  </div>
                </div>
                <span className={`product-status ${subscription?.status || 'active'}`}>
                  {statusLabel}
                  {subscription?.cancelAtPeriodEnd && (
                    <span className="cancel-notice"> (Cancels at period end)</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

          {/* Stripe Payment Component */}
        {showStripePayment && (
          <div id="payment-form-section" className="payment-form-section">
            <StripePayment
              planType={selectedPlanType || 'premium'}
              onSuccess={handleStripePaymentSuccess}
              onError={handleStripePaymentError}
              onCancel={handleStripePaymentCancel}
              isVisible={showStripePayment}
            />
          </div>
        )}


        {/* Plan Details Modal */}
        {selectedPlan && (
          <div 
            className="modal-backdrop" 
            style={{ display: 'flex' }}
            onClick={(e) => {
              if (e.target.classList.contains('modal-backdrop')) {
                setSelectedPlan(null);
              }
            }}
          >
            <div className="modal plan-details-modal" onClick={(e) => e.stopPropagation()}>
              <button 
                className="modal-close" 
                type="button" 
                onClick={() => setSelectedPlan(null)}
                aria-label="Close modal"
              >
                ×
              </button>
              
              {selectedPlan === 'lite' && (
                <div className="plan-details-content">
                  <div className="plan-details-header">
                    <div className="tier-badge lite centered">Lite</div>
                    <h2>Lite Plan</h2>
                    <div className="plan-price-large">
                      <span className="currency">£</span>
                      <span className="amount">{SUBSCRIPTION_TIERS.LITE.price}</span>
                      <span className="period">/month</span>
                    </div>
                  </div>

                  <div className="plan-features-full">
                    <h3>Features Included:</h3>
                    <ul>
                      {SUBSCRIPTION_TIERS.LITE.features.map((feature, index) => (
                        <li key={index}>
                          <span className="checkmark">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {isLiteTier() && (
                    <div className="plan-status-info">
                      <div className="status-item">
                        <span className="label">Vehicles:</span>
                        <span className="value">{vehicleCount} / Unlimited</span>
                      </div>
                      <div className="current-plan-indicator">This is your current plan</div>
                    </div>
                  )}

                  <div className="plan-actions">
                    <button 
                      className="secondary-btn"
                      onClick={() => setSelectedPlan(null)}
                    >
                      Close
                    </button>
                    {!isLiteTier() && (
                      <button 
                        className="primary-btn"
                        onClick={() => {
                          setSelectedPlan(null);
                          handleUpgrade('lite');
                        }}
                      >
                        Subscribe to Lite
                      </button>
                    )}
                  </div>
                </div>
              )}

              {selectedPlan === 'premium' && (
                <div className="plan-details-content">
                  <div className="plan-details-header">
                    <div className="tier-badge premium centered">Premium</div>
                    <h2>Premium Plan</h2>
                    <div className="plan-price-large">
                      <span className="currency">£</span>
                      <span className="amount">{SUBSCRIPTION_TIERS.PREMIUM.price}</span>
                      <span className="period">/month</span>
                    </div>
                  </div>

                  <div className="plan-features-full">
                    <h3>Features Included:</h3>
                    <ul>
                      {SUBSCRIPTION_TIERS.PREMIUM.features.map((feature, index) => (
                        <li key={index}>
                          <span className="checkmark">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {!isLiteTier() && (
                    <div className="plan-status-info">
                      <div className="status-item">
                        <span className="label">Vehicles:</span>
                        <span className="value">{vehicleCount} / Unlimited</span>
                      </div>
                      <div className="current-plan-indicator">This is your current plan</div>
                    </div>
                  )}

                  <div className="plan-actions">
                    <button 
                      className="secondary-btn"
                      onClick={() => setSelectedPlan(null)}
                    >
                      Close
                    </button>
                    {!isPremiumTier() && (
                      <button 
                        className="primary-btn"
                        onClick={() => {
                          setSelectedPlan(null);
                          handleUpgrade();
                        }}
                      >
                        Upgrade to Premium
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Billing History Card */}
        <div className="billing-card billing-history">
          <div className="card-header">
            <h3>Billing history</h3>
            <p>View and download your invoices and payment history</p>
          </div>
          
          {subscription?.syncedFromStripe ? (
            <>
              <div className="history-table">
                <div className="table-header">
                  <div className="col-date">Date</div>
                  <div className="col-type">Status</div>
                  <div className="col-amount">Amount</div>
                  <div className="col-reference">Invoice</div>
                  <div className="col-download">Download</div>
                </div>
                
                <div className="table-body">
                  {subscription?.billingHistory && subscription.billingHistory.length > 0 ? (
                    subscription.billingHistory.map((item, index) => (
                      <div key={index} className="table-row">
                        <div className="col-date">
                          {new Date(item.date).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="col-type">
                          <span className={`invoice-status ${item.status}`}>
                            {item.type}
                          </span>
                        </div>
                        <div className="col-amount">£{item.amount.toFixed(2)}</div>
                        <div className="col-reference">{item.reference}</div>
                        <div className="col-download">
                          {item.invoicePdf ? (
                            <a 
                              href={item.invoicePdf} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="download-btn"
                              title="Download invoice"
                            >
                              ↓
                            </a>
                          ) : (
                            <span className="download-btn" style={{ opacity: 0.3, cursor: 'not-allowed' }}>↓</span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-data">
                      <span>No invoices found</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button 
                  className="secondary-btn"
                  onClick={handleManageSubscription}
                  disabled={isManagingSubscription}
                >
                  {isManagingSubscription ? 'Opening...' : 'Manage All Invoices in Portal'}
                </button>
              </div>
            </>
          ) : (
            <div className="billing-history-content" style={{ 
              padding: '32px 24px',
              textAlign: 'center'
            }}>
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--text-muted)',
                margin: 0
              }}>
                Subscribe to a plan to access billing history
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
